import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LayoutDashboard } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { NotificationsDropdown } from "@/components/NotificationsDropdown";
import fixbidsLogo from "@/assets/fixbids-logo.png";

const Navbar = () => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchAvatar(session.user.id);
        checkAdminRole(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchAvatar(session.user.id);
        checkAdminRole(session.user.id);
      } else {
        setAvatarUrl(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Realtime subscription for profile avatar changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('profile-avatar-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          console.log('Profile updated:', payload);
          if (payload.new.avatar_url !== avatarUrl) {
            setAvatarUrl(payload.new.avatar_url);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, avatarUrl]);

  const fetchAvatar = async (userId: string) => {
    try {
      const { data } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("id", userId)
        .maybeSingle();
      if (data) setAvatarUrl(data.avatar_url);
    } catch (e) {
      console.warn("[Navbar] avatar fetch failed", e);
    }
  };

  const checkAdminRole = async (userId: string) => {
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    } catch (e) {
      console.warn("[Navbar] admin check failed", e);
      setIsAdmin(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
          <img src={fixbidsLogo} alt="FixBids Logo" className="h-16 w-auto object-contain" width={96} height={64} />
        </Link>
        
        <div className="flex items-center gap-2 md:gap-4">
          {user ? (
            <>
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="hidden md:flex">
                  <LayoutDashboard className="w-5 h-5" />
                </Button>
              </Link>
              {isAdmin && (
                <Link to="/admin" className="hidden md:block">
                  <Button variant="ghost" size="sm">Admin</Button>
                </Link>
              )}
              <div className="hidden md:block">
                <NotificationsDropdown userId={user.id} />
              </div>
              <Link to="/profile">
                <Avatar className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-primary transition-all">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt="Προφίλ" />}
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    <User className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
              </Link>
            </>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="ghost">Σύνδεση</Button>
              </Link>
              <Link to="/auth?register=true">
                <Button>Εγγραφή</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
